/* eslint-disable no-extra-boolean-cast */
import { Injectable, inject } from '@angular/core';
import { QueryEntity } from '@datorama/akita';
import { Observable, of } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';
import {
  AssetLiabilitiesModel,
  AssetModel,
  AssetReoModel,
  CreditModel,
  ExpenseModel,
  LiabilityModel,
  LiabilityModelExtended,
} from '../../cmg-1003/models';
import { Cmg1003ApplicationInfoQuery } from '../application-info/application-info.query';
import { Cmg1003AssetsStore } from './assetLiabilities.store';
import { AssetReoModelV2, ReoGridModel } from '../../cmg-1003/components/assets-credit-and-liabilities/reo-v2/models';
import { DirtyChecker2 } from '../core/dirty-checker-2';
import { ReoV2Utils } from '../../cmg-1003/components/assets-credit-and-liabilities/reo-v2/reo-v2.utils';
import { AssetType } from '../../cmg-1003/models/lookups/assets.lookups';

@Injectable({ providedIn: 'root' })
export class Cmg1003AssetsQuery extends QueryEntity<AssetLiabilitiesModel> {
  protected store: Cmg1003AssetsStore;
  private readonly _appQuery = inject(Cmg1003ApplicationInfoQuery);

  static instance: Cmg1003AssetsQuery;
  dirtyChecker2: DirtyChecker2 = new DirtyChecker2(this.store, this);
  constructor() {
    const store = inject(Cmg1003AssetsStore);

    super(store);
    this.store = store;

    Cmg1003AssetsQuery.instance = this;
  }

  selectActiveAssetsList() {
    return this._appQuery.selectActiveId().pipe(
      switchMap((appId) => {
        return this.selectEntity(appId, (a: AssetLiabilitiesModel) =>
          a.assetsList?.filter((t) => !t.isDeleted).map((t) => Object.assign({ applicationId: appId }, t)),
        );
      }),
    );
  }

  selectActiveCreditsList() {
    return this._appQuery.selectActiveId().pipe(
      switchMap((appId) => {
        return this.selectEntity(appId, (a: AssetLiabilitiesModel) =>
          a.creditsList?.filter((t) => !t.isDeleted).map((t) => Object.assign({ applicationId: appId }, t)),
        );
      }),
    );
  }

  selectActiveLiabilitiesList() {
    return this._appQuery.selectActiveId().pipe(
      switchMap((appId) => {
        return this.selectEntity(appId, (a: AssetLiabilitiesModel) =>
          a.liabilitiesList?.filter((t) => !t.isDeleted).map((t) => Object.assign({ applicationId: appId }, t) as LiabilityModelExtended),
        );
      }),
    );
  }

  selectActiveReoListV2() {
    return this._appQuery.selectActiveId().pipe(
      switchMap((appId) => {
        return this.selectEntity(appId, (a: AssetLiabilitiesModel) => {
          const list = a.realEstateOwnedList
            ?.filter((t) => !t.isDeleted)
            .map((t) => {
              // Need applicationId for deleting row(s) in REO table.
              return { ...this.mapReoToReoV2(t, a.liabilitiesList), applicationId: appId };
            });

          return list;
        });
      }),
    );
  }

  mapReoToReoV2(reo: AssetReoModel, liabilities: LiabilityModel[] = []): AssetReoModelV2 {
    return ReoV2Utils.mapReoToReoV2(reo, liabilities);
  }

  selectActiveReoList() {
    return this._appQuery.selectActiveId().pipe(
      switchMap((appId) => {
        return this.selectEntity(appId, (a: AssetLiabilitiesModel) =>
          a.realEstateOwnedList
            ?.filter((t) => !t.isDeleted)
            .map((t, index) => {
              const reoGridRow = new ReoGridModel();
              reoGridRow.applicationId = appId as number;
              reoGridRow.index = index;
              reoGridRow.reoId = t.reoId;
              reoGridRow.borrowerId = t.borrowerId;
              reoGridRow.isLinkedToLiabilities = t.linkedLiabilityIds?.length > 0;
              reoGridRow.marketValue = !t.marketValue ? 0 : t.marketValue;
              reoGridRow.isNetRentalIncomeOverridden = t.isNetRentalIncomeOverridden;
              reoGridRow.netRentalIncomeOverrideValue = t.netRentalIncomeOverrideValue;
              reoGridRow.netRentalIncome = !t.netRentalIncome ? 0 : t.netRentalIncome;
              reoGridRow.piti = !t.piti ? 0 : t.piti;
              reoGridRow.realEstateOwnedStatus = t.realEstateOwnedStatus;
              reoGridRow.realEstateOwnedType = t.realEstateOwnedType;
              reoGridRow.liabilityName = reoGridRow.isLinkedToLiabilities
                ? t.linkedLiabilityIds?.length > 1
                  ? 'Multiple'
                  : this.getLiabilityById(t.linkedLiabilityIds[0])?.companyName
                : '';
              return reoGridRow;
            }),
        );
      }),
    );
  }

  /**
   * Selects all REOs across all applications in the loan
   */
  selectAllReos() {
    return this.selectAll().pipe(
      map((t) => {
        // reos are seperated by applications. This map combines them into 1 array.
        const allReos = [];
        (t || []).forEach((item) => allReos.push(...(item['realEstateOwnedList'] || [])));
        return allReos;
      }),
    );
  }

  selectReoList() {
    return this._appQuery.selectActiveId().pipe(
      switchMap((appId) => {
        return this.selectEntity(appId, (a: AssetLiabilitiesModel) => a.realEstateOwnedList?.filter((t) => !t.isDeleted));
      }),
    );
  }

  selectEarnestAmt() {
    return this._appQuery.selectActiveId().pipe(
      switchMap((appId) => {
        return this.selectEntity(appId, (a: AssetLiabilitiesModel) => a.assetHeader.earnestAmt);
      }),
    );
  }

  selectSellerCredit() {
    return this._appQuery.selectActiveId().pipe(
      switchMap((appId) => {
        return this.selectEntity(appId, (a: AssetLiabilitiesModel) => a.assetHeader.sellerCredit);
      }),
    );
  }

  getAssetById(id: number, borrowerId?: number) {
    if (id === 0) {
      const emptyModel = AssetModel.createEmptyModel();
      if (borrowerId) {
        emptyModel.borrowerId = borrowerId;
      }
      return emptyModel;
    }
    const assetByAppId: AssetLiabilitiesModel = this.getEntity(this._appQuery.getActiveId()) as AssetLiabilitiesModel;
    return assetByAppId.assetsList.find((t) => t.assetId === id);
  }

  getCreditById(id: number) {
    if (id === 0) {
      const emptyModel = CreditModel.createEmptyModel();
      return emptyModel;
    }
    const assetByAppId: AssetLiabilitiesModel = this.getEntity(this._appQuery.getActiveId()) as AssetLiabilitiesModel;
    return assetByAppId.creditsList.find((t) => t.creditId === id);
  }

  selectActiveAsset(): Observable<AssetLiabilitiesModel> {
    return this._appQuery.selectActiveId().pipe(
      filter((appId) => !!appId),
      switchMap((appId) => {
        return this.selectEntity(appId) as Observable<AssetLiabilitiesModel>;
      }),
    );
  }

  getPersistentState(activeApplicationId: number) {
    const assetsModel = this.getEntity(activeApplicationId) as AssetLiabilitiesModel;
    const expenses = [];
    (assetsModel?.liabilitiesList || []).forEach((liability) => {
      if (liability.liabilityType === 2) {
        expenses.push(ExpenseModel.FromLiability(liability));
      }
    });
    const liabilitiesList = [];
    (assetsModel?.liabilitiesList || []).forEach((liability) => {
      if (liability.liabilityType !== 2) {
        liabilitiesList.push(liability);
      }
    });
    const reconfiguredAssetsModel = Object.assign({}, assetsModel, { expenses: expenses, liabilitiesList: liabilitiesList });
    return [reconfiguredAssetsModel];
  }

  getOtherAsset() {
    const assetByAppId: AssetLiabilitiesModel = this.getEntity(this._appQuery.getActiveId()) as AssetLiabilitiesModel;
    return assetByAppId.assetsList.find((t) => t.assetType === 4); // Other asset(non-banking)
  }

  getLiabilitiesList$(): Observable<LiabilityModel[]> {
    return this._appQuery.selectActiveId().pipe(
      filter((t) => !!t),
      switchMap((appId) => this.selectEntity(appId)),
      filter((t) => !!t),
      switchMap((a: AssetLiabilitiesModel) => of(a.liabilitiesList?.length > 0 ? a.liabilitiesList : null)),
    );
  }

  getAssetsBankingTotal$() {
    return this._appQuery.selectActiveId().pipe(
      filter((t) => !!t),
      switchMap((appId) => this.selectEntity(appId)),
      filter((t) => !!t),
      switchMap((a: AssetLiabilitiesModel) => of(this._calculateTotalBankingAssets(a))),
    );
  }

  private _calculateTotalBankingAssets(a: AssetLiabilitiesModel) {
    return a.assetsList?.reduce(
      (accumlator, t) => accumlator + (t.checkingSavings || []).reduce((csAcc, cs) => cs.balanceOrMarketValue + csAcc, 0),
      0,
    );
  }

  getAssetsOtherAssetsTotal$() {
    return this._appQuery.selectActiveId().pipe(
      filter((t) => !!t),
      switchMap((appId) => this.selectEntity(appId)),
      filter((t) => !!t),
      switchMap((a: AssetLiabilitiesModel) => of(this._calculateTotalOtherAssets(a))),
    );
  }

  private _calculateTotalOtherAssets(a: AssetLiabilitiesModel) {
    let total = 0;

    a.assetsList?.forEach((a) => {
      if (a && a.assetType === AssetType.OtherAssets && !a.isDeleted) {
        total += a.other?.reduce((accumlator, t) => accumlator + t.balanceOrMarketValue, 0);
      }
    });

    return total;
  }

  getTotalCredit$() {
    return this._appQuery.selectActiveId().pipe(
      filter((t) => !!t),
      switchMap((appId) => this.selectEntity(appId)),
      filter((t) => !!t),
      switchMap((a: AssetLiabilitiesModel) => of(this._calculateTotalCredit(a))),
    );
  }

  private _calculateTotalCredit(a: AssetLiabilitiesModel) {
    const notDeletedCredits = (a.creditsList || []).filter((t) => t.isDeleted === false);
    return notDeletedCredits.reduce((accumlator, t) => accumlator + t.amount, 0);
  }

  getLiabilityById(id: number, defaultBorrowerId?: number) {
    if (id === 0) {
      return LiabilityModel.createEmptyModel(defaultBorrowerId);
    }
    const assetByAppId: AssetLiabilitiesModel = this.getEntity(this._appQuery.getActiveId()) as AssetLiabilitiesModel;
    return assetByAppId.liabilitiesList.find((t) => t.liabilityId === id);
  }

  /**
   * Select all linked liabilities by REO ID
   * @param reoId REO ID to filter by
   */
  selectReoLinkedLiabilities$(reoId: number) {
    return this._appQuery.selectActiveId().pipe(
      filter((t) => !!t),
      switchMap((appId) => this.selectEntity(appId)),
      filter((t) => !!t),
      switchMap((a: AssetLiabilitiesModel) => {
        const linkedLiabilities = a.liabilitiesList?.filter(
          (t) =>
            t.liabilityType === 1 /** Banking */ &&
            (t.accountType === 3 || t.accountType === 4) /* Mortgage || Heloc */ &&
            t.isDeleted === false &&
            (t.reoId === undefined || t.reoId === null || t.reoId === reoId),
        );
        const dropdownList = [{ liabilityId: 0, companyName: 'Select' } as LiabilityModel, ...linkedLiabilities];
        return of(dropdownList);
      }),
    );
  }

  getTotalMonthlyPayments$() {
    return this._appQuery.selectActiveId().pipe(
      filter((t) => !!t),
      switchMap((appId) => this.selectEntity(appId)),
      filter((t) => !!t),
      map((assetsMainModel: AssetLiabilitiesModel) => this.calculateTotalMonthlyPayments(assetsMainModel?.liabilitiesList ?? [])),
    );
  }

  getTotalLiabilities$() {
    return this._appQuery.selectActiveId().pipe(
      filter((t) => !!t),
      switchMap((appId) => this.selectEntity(appId)),
      filter((t) => !!t),
      map((assetsMainModel: AssetLiabilitiesModel) => this.calculateTotalLiabilities(assetsMainModel?.liabilitiesList ?? [])),
    );
  }

  calculateTotalLiabilities(liabilities: LiabilityModel[]): number {
    return liabilities?.reduce((previousValue, liability) => {
      if (liability.omitted || liability.toBePaidOff || liability.notCounted) {
        return previousValue;
      }
      return previousValue + (liability?.unpaidBalance ?? 0);
    }, 0);
  }

  calculateTotalMonthlyPayments(liabilitiesList: LiabilityModel[]): number {
    return liabilitiesList.reduce((previousValue, liability) => {
      if (liability.omitted || liability.toBePaidOff || liability.notCounted) {
        return previousValue;
      }
      return previousValue + (liability?.monthlyPayment ?? 0);
    }, 0);
  }

  private _calculateTotalLiabilities(assetsMainModel: AssetLiabilitiesModel) {
    let totalLiablity = 0;
    let temp = 0;
    (assetsMainModel.liabilitiesList || []).forEach((item) => {
      if (item.liabilityType === 1 && !item.omitted && !item.isDeleted) {
        temp = !!item.unpaidBalance ? item.unpaidBalance : 0;
        totalLiablity = totalLiablity + Number(temp);
      }
    });
    return totalLiablity;
  }

  getReoById(id: number, defaultBorrowerId?: number): AssetReoModelV2 {
    if (id === 0) {
      return this.mapReoToReoV2(AssetReoModel.createEmptyModel(defaultBorrowerId));
    }
    const appId = this._appQuery.getActiveId();
    const assetByAppId: AssetLiabilitiesModel = this.getEntity(appId) as AssetLiabilitiesModel;
    const foundReo = assetByAppId.realEstateOwnedList.find((t) => t.reoId === id);
    return this.mapReoToReoV2(foundReo, assetByAppId.liabilitiesList ?? []);
  }

  reoWithSubjectPropertyPresent$(reoId: number, maxAllowedSubjectProperty: number) {
    return this._appQuery.selectActiveId().pipe(
      filter((t) => !!t),
      switchMap((appId) => this.selectEntity(appId, (t: AssetLiabilitiesModel) => t.realEstateOwnedList)),
      filter((t) => !!t),
      switchMap((reoList) =>
        of(
          (reoList || []).filter((t) => t.isDeleted !== true && t.reoId !== reoId && t.isSubjectProperty === true).length >=
            maxAllowedSubjectProperty,
        ),
      ),
    );
  }

  selectTotalReoValue$() {
    return this._appQuery.selectActiveId().pipe(
      filter((t) => !!t),
      switchMap((appId) => this.selectEntity(appId)),
      filter((t) => !!t),
      switchMap((a: AssetLiabilitiesModel) => of(this._calculateTotalReo(a))),
    );
  }

  private _calculateTotalReo(a: AssetLiabilitiesModel) {
    const notDeletedReos = !!a.realEstateOwnedList ? a.realEstateOwnedList.filter((t) => t.isDeleted === false) : [];
    return notDeletedReos.reduce((accumlator, t) => accumlator + (!!t.marketValue ? t.marketValue : 0), 0);
  }

  calculateNetWorth$() {
    return this._appQuery.selectActiveId().pipe(
      filter((t) => !!t),
      switchMap((appId) => this.selectEntity(appId)),
      filter((t) => !!t),
      switchMap((a: AssetLiabilitiesModel) =>
        of(
          this._calculateTotalBankingAssets(a) +
            this._calculateTotalOtherAssets(a) +
            this._calculateTotalCredit(a) +
            this._calculateTotalReo(a) -
            this._calculateTotalLiabilities(a),
        ),
      ),
    );
  }

  getNetRentalIncome$(borrowerId: number) {
    return this._appQuery.selectActiveId().pipe(
      filter((t) => !!t),
      switchMap((appId) => this.selectEntity(appId, (t: AssetLiabilitiesModel) => t.realEstateOwnedList)),
      filter((t) => !!t),
      map((reoList) =>
        reoList
          .filter((r) => r.borrowerId === borrowerId && !r.isDeleted && r.realEstateOwnedStatus === 3 /** Retained Rental */)
          .map((t) => (t.isNetRentalIncomeOverridden ? (t.netRentalIncomeOverrideValue ?? 0) : (t.netRentalIncome ?? 0)))
          .reduce((a, b) => a + b, 0),
      ),
    );
  }

  /** Total NRI across all applications and borrowers — used for TRID 6-piece income check. */
  getTotalNetRentalIncome$() {
    return this.selectAll().pipe(
      map((allAppsAssets: AssetLiabilitiesModel[]) => {
        const allReos: AssetReoModel[] = [];
        (allAppsAssets || []).forEach((app) => allReos.push(...(app.realEstateOwnedList || [])));
        return allReos
          .filter((r) => !r.isDeleted && r.realEstateOwnedStatus === 3 /** Retained Rental */)
          .map((t) => (t.isNetRentalIncomeOverridden ? (t.netRentalIncomeOverrideValue ?? 0) : (t.netRentalIncome ?? 0)))
          .reduce((a, b) => a + b, 0);
      }),
    );
  }
}
